import { UseQueryResult, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ContractRequestDto,
  ContractZoneDto,
  ResponseDtoPageContractDto,
  ResponseDtoContractMetadataDto,
  ResponseDtoListContractZoneDto,
  ResponseDtoContractZoneDto,
  ResponseDtoVoid,
  CreatingContractReq,
  ContractUpsertRequestDto,
  ResponseDtoContractDto,
  ContractRouteDto,
  ResponseDtoListContractRouteDto,
  ResponseDtoContractRouteDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';

/**
 * Filter interface for contract queries
 */
export interface ContractFilters {
  serviceProviderIds?: number[];
  startDates?: string[];
  endDates?: string[];
  zoneCounts?: number[];
  routeCounts?: number[];
  contractFlowCounts?: number[];
  shipmentCounts?: number[];
}

// Interface for pagination and search parameters
interface ContractListParams {
  page?: number;
  size?: number;
  sortingColum?: string;
  sortingDirection?: 'ASC' | 'DESC';
}

/**
 * Hook to fetch contracts list with pagination and filters
 * @param params Pagination and sorting parameters
 * @param filters Filter parameters
 * @returns Query result with paginated contracts data
 */
export const useContractList = (params: any, filters?: ContractFilters): UseQueryResult<ResponseDtoPageContractDto> =>
  useQuery(
    ['contracts', params, filters],
    async () => {
      // Build request data with filters and pagination
      const requestDto: ContractRequestDto = {
        paginationRequest: {
          page: params.page,
          size: params.size,
          // Support both legacy field names (sortingColum/sortingDirection) and
          // the flat requestBody from useSettingsTable (sortBy/sortDirection)
          sortingColum: params.sortingColum ?? params.sortBy,
          sortingDirection: params.sortingDirection ?? params.sortDirection,
        },
      };

      // Support both calling conventions:
      //   two-arg: useContractList(paginationParams, filters)
      //   single-arg: useContractList({ ...paginationParams, ...filters })  ← used by useSettingsTable
      const effectiveFilters: ContractFilters = filters ?? params;
      if (effectiveFilters.serviceProviderIds?.length) requestDto.serviceProviderIds = effectiveFilters.serviceProviderIds;
      if (effectiveFilters.startDates?.length) requestDto.startDates = effectiveFilters.startDates;
      if (effectiveFilters.endDates?.length) requestDto.endDates = effectiveFilters.endDates;
      if (effectiveFilters.zoneCounts?.length) requestDto.zoneCounts = effectiveFilters.zoneCounts;
      if (effectiveFilters.routeCounts?.length) requestDto.routeCounts = effectiveFilters.routeCounts;
      if (effectiveFilters.contractFlowCounts?.length) requestDto.contractFlowCounts = effectiveFilters.contractFlowCounts;
      if (effectiveFilters.shipmentCounts?.length) requestDto.shipmentCounts = effectiveFilters.shipmentCounts;

      const response = await tFinancialService.api.getAllContracts(requestDto);
      return response.data;
    },
    {
      keepPreviousData: true,
    }
  );

/**
 * Hook to fetch contracts metadata for filtering options
 * This provides filter options for service providers, dates, counts, etc.
 */
export const useContractsMetadata = (filters: ContractFilters = {}): UseQueryResult<ResponseDtoContractMetadataDto> =>
  useQuery(
    ['contractsMetadata', filters],
    async (): Promise<ResponseDtoContractMetadataDto> => {
      // Build request data with filters
      const requestDto: ContractRequestDto = {
        paginationRequest: {
          page: 0,
          size: 10,
        },
      };

      // Add filters if they exist
      if (filters.serviceProviderIds?.length) requestDto.serviceProviderIds = filters.serviceProviderIds;
      if (filters.startDates?.length) requestDto.startDates = filters.startDates;
      if (filters.endDates?.length) requestDto.endDates = filters.endDates;
      if (filters.zoneCounts?.length) requestDto.zoneCounts = filters.zoneCounts;
      if (filters.routeCounts?.length) requestDto.routeCounts = filters.routeCounts;
      if (filters.contractFlowCounts?.length) requestDto.contractFlowCounts = filters.contractFlowCounts;
      if (filters.shipmentCounts?.length) requestDto.shipmentCounts = filters.shipmentCounts;

      return (await tFinancialService.api.getAllContractsMetaData(requestDto)).data;
    },
    {
      staleTime: 5 * 60 * 1000,
      refetchOnWindowFocus: false,
    }
  );

/**
 * Hook to fetch contract zones by contract ID
 * @param contractId Contract ID to fetch zones for
 * @returns Query result with contract zones data
 */
export const useContractZones = (contractId: string | number | null): UseQueryResult<ResponseDtoListContractZoneDto> =>
  useQuery(
    ['contractZones', contractId],
    async () => {
      if (!contractId) throw new Error('Contract ID is required');
      const response = await tFinancialService.api.getContractZones(String(contractId));
      return response.data;
    },
    {
      enabled: !!contractId,
      staleTime: 5 * 60 * 1000,
    }
  );

/**
 * Hook to upsert (create or update) a contract zone
 * @returns Mutation function for upserting contract zones
 */
export const useUpsertContractZone = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ContractZoneDto): Promise<ResponseDtoContractZoneDto> => {
      const response = await tFinancialService.api.upsertContractZone(data);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate contract zones query for this contract
        queryClient.invalidateQueries(['contractZones', variables.contractId]);
        // Invalidate contracts list to update zone counts
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to delete a contract zone
 * @returns Mutation function for deleting contract zones
 */
export const useDeleteContractZone = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ contractZoneId, contractId }: { contractZoneId: string; contractId: string }): Promise<{ response: ResponseDtoVoid; contractId: string }> => {
      const response = await tFinancialService.api.deleteContractZone(contractZoneId);
      return { response: response.data, contractId };
    },
    {
      onSuccess: (data) => {
        // Invalidate contract zones query for this contract
        queryClient.invalidateQueries(['contractZones', data.contractId]);
        // Invalidate contracts list to update zone counts
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to create a new contract
 * @returns Mutation function for creating contracts
 */
export const useCreateContract = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: CreatingContractReq): Promise<ResponseDtoContractDto> => {
      const response = await tFinancialService.api.createContract(data);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate contracts list to show new contract
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to upsert (create or update) a contract
 * @returns Mutation function for upserting contracts
 */
export const useUpsertContract = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ContractUpsertRequestDto): Promise<ResponseDtoContractDto> => {
      const response = await tFinancialService.api.upsertContract(data);
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate contracts list to show updated contract
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to delete a contract
 * @returns Mutation function for deleting contracts
 */
export const useDeleteContract = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ contractId, forceDeleteContractFlow = false }: { contractId: string; forceDeleteContractFlow?: boolean }): Promise<ResponseDtoVoid> => {
      const response = await tFinancialService.api.deleteContract(contractId, { forceDeleteContractFlow });
      return response.data;
    },
    {
      onSuccess: () => {
        // Invalidate contracts list to remove deleted contract
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to fetch contract routes by contract ID
 * @param contractId - The contract ID to fetch routes for
 * @returns Query result containing list of contract routes
 */
export const useContractRoutes = (contractId: string): UseQueryResult<ResponseDtoListContractRouteDto> =>
  useQuery(
    ['contractRoutes', contractId],
    async () => {
      const response = await tFinancialService.api.getContractRoutes(contractId);
      return response.data;
    },
    {
      enabled: !!contractId,
    }
  );

/**
 * Hook to upsert (create or update) a contract route
 * @returns Mutation function for upserting contract routes
 */
export const useUpsertContractRoute = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async (data: ContractRouteDto): Promise<ResponseDtoContractRouteDto> => {
      const response = await tFinancialService.api.upsertContractRoute(data);
      return response.data;
    },
    {
      onSuccess: (_, variables) => {
        // Invalidate contract routes query for this contract
        queryClient.invalidateQueries(['contractRoutes', variables.contractId]);
        // Invalidate contracts list to update route counts
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to delete a contract route
 * @returns Mutation function for deleting contract routes
 */
export const useDeleteContractRoute = () => {
  const queryClient = useQueryClient();

  return useMutation(
    async ({ contractRouteId, contractId }: { contractRouteId: string; contractId: string }): Promise<{ response: ResponseDtoVoid; contractId: string }> => {
      const response = await tFinancialService.api.deleteContractRoute(contractRouteId);
      return { response: response.data, contractId };
    },
    {
      onSuccess: (data) => {
        // Invalidate contract routes query for this contract
        queryClient.invalidateQueries(['contractRoutes', data.contractId]);
        // Invalidate contracts list to update route counts
        queryClient.invalidateQueries(['contracts']);
      },
    }
  );
};

/**
 * Hook to fetch all contracts for a given service provider (for validity period dropdowns)
 * @param serviceProviderId - The numeric service provider ID
 */
export const useContractsByServiceProvider = (serviceProviderId?: number): UseQueryResult<ResponseDtoPageContractDto> =>
  useQuery(
    ['contractsByServiceProvider', serviceProviderId],
    async () => {
      const { data } = await tFinancialService.api.getContractsByServiceProvider(serviceProviderId!, {
        page: 0,
        size: 100,
      });
      return data;
    },
    {
      enabled: !!serviceProviderId,
      staleTime: 5 * 60 * 1000,
    }
  );
